import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './store/authStore';
import { AppShell } from './components/AppShell';
import { LoadingOrb } from './components/LoadingOrb';
import { PageTransition } from './components/PageTransition';
import { SetupProvider } from './store/setupStore';
import { AlertToastProvider } from './store/alertToastStore';
import { DeviceWebSocketProvider } from './store/deviceWebSocketStore';
import { SuccessToastProvider } from './store/successToastStore';
// Lazy loaded components
const SplashScreen = lazy(() => import('./pages/SplashScreen').then(m => ({
  default: m.SplashScreen
})));
const IntroSlides = lazy(() => import('./pages/IntroSlides').then(m => ({
  default: m.IntroSlides
})));
const RegisterScreen = lazy(() => import('./pages/RegisterScreen').then(m => ({
  default: m.RegisterScreen
})));
const LoginScreen = lazy(() => import('./pages/LoginScreen').then(m => ({
  default: m.LoginScreen
})));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({
  default: m.ForgotPassword
})));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({
  default: m.ResetPassword
})));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({
  default: m.Dashboard
})));
const MonitorScreen = lazy(() => import('./pages/MonitorScreen').then(m => ({
  default: m.MonitorScreen
})));
const SessionsList = lazy(() => import('./pages/history/SessionsList').then(m => ({
  default: m.SessionsList
})));
const SessionDetail = lazy(() => import('./pages/history/SessionDetail').then(m => ({
  default: m.SessionDetail
})));
const ShareReport = lazy(() => import('./pages/history/ShareReport').then(m => ({
  default: m.ShareReport
})));
const AppointmentsList = lazy(() => import('./pages/appointments/AppointmentsList').then(m => ({
  default: m.AppointmentsList
})));
const LearnHome = lazy(() => import('./pages/learn/LearnHome').then(m => ({
  default: m.LearnHome
})));
const LearnSection = lazy(() => import('./pages/learn/LearnSection').then(m => ({
  default: m.LearnSection
})));
const MotherScreen = lazy(() => import('./pages/mother/MotherScreen').then(m => ({
  default: m.MotherScreen
})));
const BabyScreen = lazy(() => import('./pages/baby/BabyScreen').then(m => ({
  default: m.BabyScreen
})));
const AuroraChat = lazy(() => import('./pages/aurora/AuroraChat').then(m => ({
  default: m.AuroraChat
})));
const AuroraHistory = lazy(() => import('./pages/aurora/AuroraHistory').then(m => ({
  default: m.AuroraHistory
})));
const AuroraConversationView = lazy(() => import('./pages/aurora/AuroraConversationView').then(m => ({
  default: m.AuroraConversationView
})));
const AlertsList = lazy(() => import('./pages/alerts/AlertsList').then(m => ({
  default: m.AlertsList
})));
const AlertDetail = lazy(() => import('./pages/alerts/AlertDetail').then(m => ({
  default: m.AlertDetail
})));
const ProfileScreen = lazy(() => import('./pages/profile/ProfileScreen').then(m => ({
  default: m.ProfileScreen
})));
const EditProfile = lazy(() => import('./pages/profile/EditProfile').then(m => ({
  default: m.EditProfile
})));
const DeviceManagement = lazy(() => import('./pages/profile/DeviceManagement').then(m => ({
  default: m.DeviceManagement
})));
const DeviceEntry = lazy(() => import('./pages/setup/DeviceEntry').then(m => ({
  default: m.DeviceEntry
})));
const WaitingActivation = lazy(() => import('./pages/setup/WaitingActivation').then(m => ({
  default: m.WaitingActivation
})));
const SetupSuccess = lazy(() => import('./pages/setup/SetupSuccess').then(m => ({
  default: m.SetupSuccess
})));
// Route Guard for unauthenticated users trying to access protected routes
function ProtectedRoute({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isAuthenticated,
    isLoading
  } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return <FullScreenLoader data-id="element-0" data-mp="h59jsrs5f68d" />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/splash" state={{
      from: location
    }} replace data-id="element-1" data-mp="c9kc215z6j0z" />;
  }
  return <>{children}</>;
}
// Route Guard for authenticated users trying to access auth screens
function AuthRoute({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isAuthenticated,
    isLoading
  } = useAuth();
  if (isLoading) {
    return <FullScreenLoader data-id="element-2" data-mp="vwiaz5bp8znm" />;
  }
  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace data-id="element-3" data-mp="vybam0nwajm4" />;
  }
  return <>{children}</>;
}
function FullScreenLoader() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const startTime = Date.now();
    const duration = 1500;
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      setProgress(eased * 100);
      if (t < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, []);
  return <div className="min-h-screen w-full bg-secondary flex flex-col items-center justify-center" data-id="element-4" data-mp="29wljuaojrkn1">
      <LoadingOrb data-id="element-5" data-mp="2eu4uz1py7har" />
      <h1 className="font-sans text-[28px] font-semibold text-text-primary tracking-[4px] uppercase mt-6 mb-6" data-id="element-6" data-mp="2eun1cr6brbwe">
        Aurora Care
      </h1>
      <div className="w-[120px] h-[3px] bg-border rounded-pill overflow-hidden" data-id="element-7" data-mp="2nvuocd3e4qb6">
        <div className="h-full bg-primary rounded-pill transition-none" style={{
        width: `${progress}%`
      }} data-id="element-8" data-mp="2supfc2bihl6d" />
      </div>
    </div>;
}
function AppRoutes() {
  return <Suspense fallback={<FullScreenLoader data-id="element-10" data-mp="yhpthrgjfuqn" />} data-id="element-9" data-mp="yg4yk0qba9s9">
      <Routes data-id="element-11" data-mp="yh6jcwx4qan6">
        {/* Public / Auth Routes */}
        <Route path="/" element={<Navigate to="/splash" replace data-id="element-13" data-mp="21slv64wn1cwn" />} data-id="element-12" data-mp="21r29tzw4kbo0" />
        <Route path="/splash" element={<AuthRoute data-id="element-15" data-mp="1wtsmcpsaqa2t">
              <PageTransition type="fade" data-id="element-16" data-mp="1wukbbhre19k4">
                <SplashScreen data-id="element-17" data-mp="2ass4delazqq0" />
              </PageTransition>
            </AuthRoute>} data-id="element-14" data-mp="21tf153nrvydq" />
        <Route path="/intro" element={<AuthRoute data-id="element-19" data-mp="25viexyxwzf26">
              <PageTransition type="fade" data-id="element-20" data-mp="2kpwpdb3exg58">
                <IntroSlides data-id="element-21" data-mp="2kovbkyv0ifjt" />
              </PageTransition>
            </AuthRoute>} data-id="element-18" data-mp="25sw7s0exr1kh" />
        <Route path="/register" element={<AuthRoute data-id="element-23" data-mp="2tj037khyzv0e">
              <PageTransition type="slideUp" data-id="element-24" data-mp="2ti8hd1e6dbnb">
                <RegisterScreen data-id="element-25" data-mp="2pheso5swrstd" />
              </PageTransition>
            </AuthRoute>} data-id="element-22" data-mp="2fr3c9js1fr5l" />
        <Route path="/login" element={<AuthRoute data-id="element-27" data-mp="33etmdmq72mr5">
              <PageTransition type="slideUp" data-id="element-28" data-mp="2yhupwxj263ps">
                <LoginScreen data-id="element-29" data-mp="2yj5u2ygf1dhf" />
              </PageTransition>
            </AuthRoute>} data-id="element-26" data-mp="33go5cvhwo4sl" />
        <Route path="/forgot-password" element={<AuthRoute data-id="element-31" data-mp="3n2zih7vwe08b">
              <PageTransition type="slideUp" data-id="element-32" data-mp="3n1el7ai7836r">
                <ForgotPassword data-id="element-33" data-mp="385gw2c6ozz41" />
              </PageTransition>
            </AuthRoute>} data-id="element-30" data-mp="3i463tc4vk36k" />
        <Route path="/reset-password" element={<PageTransition type="slideUp" data-id="element-35" data-mp="3d6mpydc39sf5">
              <ResetPassword data-id="element-36" data-mp="2zcvev0ipm0hb" />
            </PageTransition>} data-id="element-34" data-mp="3d4a0lf404fs2" />

        {/* Protected Routes */}
        <Route path="/app/*" element={<ProtectedRoute data-id="element-38" data-mp="2pez3c7lv13io">
              <DashboardRoutes data-id="element-39" data-mp="2pfqth2wou91k" />
            </ProtectedRoute>} data-id="element-37" data-mp="344wy56eaorgu" />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/splash" replace data-id="element-41" data-mp="2uebsx0448ooy" />} data-id="element-40" data-mp="2uf3nq8pw2ie6" />
      </Routes>
    </Suspense>;
}
function DashboardRoutes() {
  return <Suspense fallback={<FullScreenLoader data-id="element-43" data-mp="2kfmlk19uimmy" />} data-id="element-42" data-mp="2ggn7nr64zfjd">
      <Routes data-id="element-44" data-mp="2ket9qlw156xy">
        {/* Setup flow doesn't use the AppShell */}
        <Route path="setup/*" element={<SetupProvider data-id="element-46" data-mp="30uly7qfn4fri">
              <Routes data-id="element-47" data-mp="2vwaodfpci59a">
                <Route path="" element={<PageTransition type="slideUp" data-id="element-49" data-mp="2qwxrs38o9jti">
                      <DeviceEntry data-id="element-50" data-mp="2qvmpy8ysem51" />
                    </PageTransition>} data-id="element-48" data-mp="2vvr43sc834s3" />
                <Route path="waiting" element={<PageTransition type="slideRight" data-id="element-52" data-mp="2hy2l6q1pu7po">
                      <WaitingActivation data-id="element-53" data-mp="2d62od6gxzuxb" />
                    </PageTransition>} data-id="element-51" data-mp="2lxuy1ey5700u" />
                <Route path="success" element={<PageTransition type="slideUp" data-id="element-55" data-mp="23854swa12mb1">
                      <SetupSuccess data-id="element-56" data-mp="239g5uoc39fyg" />
                    </PageTransition>} data-id="element-54" data-mp="285wwbp6rg6ep" />
              </Routes>
            </SetupProvider>} data-id="element-45" data-mp="30v3sihiqsjws" />

        {/* Monitor screen is full screen, outside AppShell */}
        <Route path="monitor" element={<PageTransition type="slideUp" data-id="element-58" data-mp="9qz44vg1fzqq">
              <MonitorScreen data-id="element-59" data-mp="dpye4ugmmmp1" />
            </PageTransition>} data-id="element-57" data-mp="9p643yzxkdrp" />

        {/* All other app routes use the AppShell */}
        <Route path="*" element={<AppShell data-id="element-61" data-mp="np77pm7kxzav">
              <Routes data-id="element-62" data-mp="nnmdxyt3ake2">
                <Route path="dashboard" element={<PageTransition type="fade" data-id="element-64" data-mp="3m7vzpuklf37y">
                      <Dashboard data-id="element-65" data-mp="3r54k81t7rjcd" />
                    </PageTransition>} data-id="element-63" data-mp="nnw4bni1qtka" />
                <Route path="mother" element={<PageTransition type="fade" data-id="element-67" data-mp="4pta8uan66fm">
                      <MotherScreen data-id="element-68" data-mp="4ql522wf004u" />
                    </PageTransition>} data-id="element-66" data-mp="3w4qty19u2hgf" />
                <Route path="baby" element={<PageTransition type="fade" data-id="element-70" data-mp="38dlidyaxebbx">
                      <BabyScreen data-id="element-71" data-mp="2f7598z3d63dm" />
                    </PageTransition>} data-id="element-69" data-mp="33fa5tft8xmq0" />
                <Route path="history" element={<PageTransition type="fade" data-id="element-73" data-mp="2k8182y8uyzc6">
                      <SessionsList data-id="element-74" data-mp="2p6ciba32fbux" />
                    </PageTransition>} data-id="element-72" data-mp="2k5yb1u9ag5xy" />
                <Route path="history/:sessionId" element={<PageTransition type="slideRight" data-id="element-76" data-mp="1whyoz7mx93pp">
                      <SessionDetail data-id="element-77" data-mp="21fgwctlajavl" />
                    </PageTransition>} data-id="element-75" data-mp="2u32x7u34w1er" />
                <Route path="history/:sessionId/share" element={<PageTransition type="slideRight" data-id="element-79" data-mp="2b5s1ukkmhs4l">
                      <ShareReport data-id="element-80" data-mp="2b7mkttcc3a61" />
                    </PageTransition>} data-id="element-78" data-mp="26e9zbs2ybjhi" />
                <Route path="appointments" element={<PageTransition type="fade" data-id="element-82" data-mp="1imw7dn8pppx2">
                      <AppointmentsList data-id="element-83" data-mp="3vyh5dqf2j13i" />
                    </PageTransition>} data-id="element-81" data-mp="1dobfqc7j0ek8" />
                <Route path="learn" element={<PageTransition type="fade" data-id="element-85" data-mp="3mxrcovl3tqu0">
                      <LearnHome data-id="element-86" data-mp="3n0namjwycfig" />
                    </PageTransition>} data-id="element-84" data-mp="3rz8aujc7ez6w" />
                <Route path="learn/:slug" element={<PageTransition type="slideRight" data-id="element-88" data-mp="3d2hb28ymblgr">
                      <LearnSection data-id="element-89" data-mp="388v4myj1sdte" />
                    </PageTransition>} data-id="element-87" data-mp="3i23qmp1x5dsq" />
                <Route path="aurora" element={<PageTransition type="fade" data-id="element-91" data-mp="2zaatcifth6f7">
                      <AuroraChat data-id="element-92" data-mp="2z7ewyyjmapsv" />
                    </PageTransition>} data-id="element-90" data-mp="34946g9r6yveu" />
                <Route path="aurora/history" element={<PageTransition type="slideRight" data-id="element-94" data-mp="2pdem0g1nmqc8">
                      <AuroraHistory data-id="element-95" data-mp="2fmoionyz2zgt" />
                    </PageTransition>} data-id="element-93" data-mp="2pb22hyg907x2" />
                <Route path="aurora/history/:conversationId" element={<PageTransition type="slideRight" data-id="element-97" data-mp="26okxjsrwg4pj">
                      <AuroraConversationView data-id="element-98" data-mp="2anlboxzfmj67" />
                    </PageTransition>} data-id="element-96" data-mp="2ko41089rmrvi" />
                <Route path="alerts" element={<PageTransition type="fade" data-id="element-100" data-mp="33bz859avh7fn">
                      <AlertsList data-id="element-101" data-mp="33ev8f4a70879" />
                    </PageTransition>} data-id="element-99" data-mp="2yf8s2lz2ibu8" />
                <Route path="alerts/:alertId" element={<PageTransition type="slideRight" data-id="element-103" data-mp="2tkuvig8o5ynz">
                      <AlertDetail data-id="element-104" data-mp="3hc0ayuap6oxr" />
                    </PageTransition>} data-id="element-102" data-mp="2tn7lngokzfc4" />
                <Route path="profile" element={<PageTransition type="fade" data-id="element-106" data-mp="1an8dgpdzdr3h">
                      <ProfileScreen data-id="element-107" data-mp="15pgan0bsskki" />
                    </PageTransition>} data-id="element-105" data-mp="3m53636v4i6g2" />
                <Route path="profile/edit" element={<PageTransition type="slideRight" data-id="element-109" data-mp="1ff9iprcqehk0">
                      <EditProfile data-id="element-110" data-mp="1ffrrfngkyo8t" />
                    </PageTransition>} data-id="element-108" data-mp="1ke319y6vkyr1" />
                <Route path="profile/devices" element={<PageTransition type="slideRight" data-id="element-112" data-mp="ms5eholgq79a">
                      <DeviceManagement data-id="element-113" data-mp="11oe9gdyblyfw" />
                    </PageTransition>} data-id="element-111" data-mp="rpxfd84353pm" />
                <Route path="*" element={<Navigate to="dashboard" replace data-id="element-115" data-mp="wn71oew6gz98" />} data-id="element-114" data-mp="woicgyo1j79u" />
              </Routes>
            </AppShell>} data-id="element-60" data-mp="iq41vzjrzp1u" />
      </Routes>
    </Suspense>;
}
export function App() {
  return <AuthProvider data-id="element-116" data-mp="2835e8sqke3f7">
      <AlertToastProvider data-id="element-117" data-mp="283nkmi6xxy0u">
        <DeviceWebSocketProvider data-id="element-118" data-mp="hmtf5etyb6c6">
          <SuccessToastProvider data-id="element-119" data-mp="hnl80hw5ypyr">
            <BrowserRouter data-id="element-120" data-mp="hoo18wgyl4hu">
              <AppRoutes data-id="element-121" data-mp="mlwld2lo3ilq" />
            </BrowserRouter>
          </SuccessToastProvider>
        </DeviceWebSocketProvider>
      </AlertToastProvider>
    </AuthProvider>;
}